import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import InnerLayout from "../../components/layouts/inner-layout";
import SettingsItemContainer from "../../components/settings/settings-item-container";
import settingsData from "./settings-data";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../../components/layouts/main-layout";
import ItemsInputFieldStandalone from "../../components/forms/standalone-input-field";

// Section colours reuse the reports palette (--reports-<tone>_text/_bg);
// a section without a tone falls back to the primary colour.
const toneColors = (tone) =>
  tone
    ? { fg: `var(--reports-${tone}_text)`, bg: `var(--reports-${tone}_bg)` }
    : { fg: "var(--primary)", bg: "var(--row-selection-color)" };

// Settings home: a section sidebar on the left that scrolls to each section,
// a header card with search, and one card grid per section. Settings with an
// `input` component open inline below a breadcrumb; the rest navigate.
const Settings = () => {
  const { permissionFn } = useContext(DataContext);
  const navigate = useNavigate();
  const hasViewAccess = (moduleName) => {
    if (!moduleName) return true;
    const permission = permissionFn(moduleName);
    return permission?.read === true || permission?.all === true;
  };

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const sectionRefs = useRef({});

  // Sections the user can see, each with the items matching the search.
  // A section with no children is shown as a single card of itself.
  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (item) =>
      !q ||
      `${item?.title ?? ""} ${item?.desc ?? ""} ${item?.badge ?? ""}`
        .toLowerCase()
        .includes(q);
    return settingsData
      .map((section, index) => {
        const children = section?.childrens?.length
          ? section.childrens
          : [section];
        const items = children.filter(
          (item) => item && hasViewAccess(item?.module_name) && matches(item)
        );
        return { ...section, index, items };
      })
      .filter((section) => section.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, permissionFn]);

  const totalCount = sections.reduce((n, s) => n + s.items.length, 0);

  const handleItemClick = (index, setting, section) => {
    if (setting.input) {
      setSelectedComponent(setting.input);
      setSelectedSetting({ ...setting, sectionTitle: section?.title });
    } else {
      navigate(setting.path);
    }
  };

  const goBack = () => {
    setSelectedSetting(null);
    setSelectedComponent(null);
  };

  // While a sidebar click is scrolling the page, keep the clicked entry
  // highlighted instead of recomputing it from the scroll position.
  const clickScrollLock = useRef(null);
  const releaseClickScrollLock = (delay) => {
    clearTimeout(clickScrollLock.current);
    clickScrollLock.current = setTimeout(() => {
      clickScrollLock.current = null;
    }, delay);
  };

  const scrollToSection = (index) => {
    setActiveIndex(index);
    releaseClickScrollLock(1000);
    sectionRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Highlight the sidebar entry from the scroll position: the last section
  // whose top has passed the top of the scroller, or the last section once
  // the page is scrolled to the bottom (short final sections never reach the top).
  useEffect(() => {
    if (selectedSetting) return;
    const root = document.querySelector(".innerLayoutScroller");
    if (!root) return;
    const updateActive = () => {
      if (clickScrollLock.current) {
        releaseClickScrollLock(150);
        return;
      }
      if (sections.length === 0) return;
      const atBottom =
        root.scrollTop + root.clientHeight >= root.scrollHeight - 4;
      if (atBottom) {
        setActiveIndex(sections[sections.length - 1].index);
        return;
      }
      const rootTop = root.getBoundingClientRect().top;
      let current = sections[0].index;
      sections.forEach((section) => {
        const el = sectionRefs.current[section.index];
        if (el && el.getBoundingClientRect().top - rootTop <= 120) {
          current = section.index;
        }
      });
      setActiveIndex(current);
    };
    updateActive();
    root.addEventListener("scroll", updateActive, { passive: true });
    return () => root.removeEventListener("scroll", updateActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, selectedSetting]);

  // Old "#report-N" links still jump to that section.
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith("#report-")) {
        scrollToSection(Number(hash.replace("#report-", "")));
      }
    };
    window.addEventListener("hashchange", handleHashChange, false);
    handleHashChange();
    return () =>
      window.removeEventListener("hashchange", handleHashChange, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A setting opened inline: breadcrumb back to Settings + its component.
  if (selectedSetting) {
    return (
      <InnerLayout title="Settings">
        <div className="px-6 pt-5">
          <div className="flex items-center gap-1.5 text-sm">
            <button
              type="button"
              onClick={goBack}
              className="flex items-center gap-1.5 text-[var(--description-color)] hover:text-[var(--primary)]"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Settings
            </button>
            {selectedSetting.sectionTitle && (
              <>
                <span className="text-[var(--description-color)]">/</span>
                <span className="text-[var(--description-color)]">
                  {selectedSetting.sectionTitle}
                </span>
              </>
            )}
            <span className="text-[var(--description-color)]">/</span>
            <span className="fw_600 text-[var(--heading-color)]">
              {selectedSetting.title}
            </span>
          </div>
          <div className="w-full mt-4">{selectedComponent}</div>
        </div>
      </InnerLayout>
    );
  }

  return (
    <InnerLayout title="Settings">
      <div className="flex min-h-full bg-slate-50">
        {/* Section sidebar */}
        <aside className="hidden md:block w-64 shrink-0 border-r border-[var(--light-border-color)] bg-white">
          <nav className="sticky top-0 p-4">
            <div className="px-2 pb-3 text-[11px] fw_600 uppercase tracking-wider text-[var(--description-color)]">
              Sections
            </div>
            <ul className="flex flex-col gap-1">
              {sections.map((section) => {
                const active = section.index === activeIndex;
                const colors = toneColors(section.tone);
                const Icon = section.icon;
                return (
                  <li key={section.index}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(section.index)}
                      className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm text-left transition-colors ${
                        active
                          ? "bg-slate-50 text-[var(--heading-color)] fw_600"
                          : "text-[var(--heading-color)] hover:bg-[var(--row-hover-color)]"
                      }`}
                    >
                      <span
                        className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                        style={
                          active
                            ? { background: colors.fg, color: "#fff" }
                            : { background: colors.bg, color: colors.fg }
                        }
                      >
                        {Icon && <Icon size={16} strokeWidth={1.9} />}
                      </span>
                      <span className="flex-1 min-w-0 leading-tight">
                        {section.title}
                      </span>
                      <span
                        className={`shrink-0 min-w-[1.75rem] h-6 px-2 rounded-full text-[11px] fw_600 flex items-center justify-center ${
                          active
                            ? "bg-white border border-[var(--light-border-color)] text-[var(--heading-color)]"
                            : "text-[var(--description-color)]"
                        }`}
                      >
                        {section.items.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6">
          {/* Header card: title and search */}
          <div className="rounded-2xl border border-[var(--light-border-color)] bg-white p-5 sm:p-7">
            <h1 className="text-2xl fw_700 text-[var(--heading-color)]">
              Settings
            </h1>
            <p className="text-sm text-[var(--description-color)] mt-1">
              Configure your store, users, master data, discounts and accounts
              — all in one place.
            </p>

            <div className="mt-5">
              <ItemsInputFieldStandalone
                isLabelReq={false}
                leftIcon={
                  <Search
                    className="text-[var(--stand-alone-place-color)]"
                    size={"15px"}
                  />
                }
                placeholder="Search settings"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {query && (
              <div className="mt-3 text-xs text-[var(--description-color)]">
                {totalCount} result{totalCount === 1 ? "" : "s"} for “
                {query.trim()}”
              </div>
            )}
          </div>

          {/* Sections */}
          <div className="mt-8 flex flex-col gap-10">
            {sections.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-20">
                <div className="w-12 h-12 rounded-full bg-[var(--row-selection-color)] flex items-center justify-center">
                  <Search size={22} className="text-[var(--primary)]" />
                </div>
                <div className="mt-4 text-sm fw_600 text-[var(--heading-color)]">
                  No settings match “{query.trim()}”
                </div>
                <div className="mt-1 text-xs text-[var(--description-color)]">
                  Try a shorter word, like “tax” or “print”.
                </div>
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="mt-4 text-sm fw_600 text-[var(--primary)] hover:underline"
                >
                  Clear search
                </button>
              </div>
            ) : (
              sections.map((section) => {
                const colors = toneColors(section.tone);
                const SectionIcon = section.icon;
                return (
                  <section
                    key={section.index}
                    id={`report-${section.index}`}
                    data-index={section.index}
                    ref={(el) => (sectionRefs.current[section.index] = el)}
                    className="scroll-mt-6"
                  >
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ background: colors.bg, color: colors.fg }}
                        >
                          {SectionIcon && (
                            <SectionIcon size={18} strokeWidth={1.9} />
                          )}
                        </span>
                        <div className="min-w-0">
                          <h2 className="text-base fw_700 text-[var(--heading-color)]">
                            {section.title}
                          </h2>
                          {section.desc && (
                            <p className="text-xs text-[var(--description-color)] mt-0.5">
                              {section.desc}
                            </p>
                          )}
                        </div>
                      </div>
                      <span
                        className="shrink-0 h-6 px-2.5 rounded-full text-xs fw_600 flex items-center"
                        style={{ background: colors.bg, color: colors.fg }}
                      >
                        {section.items.length} item
                        {section.items.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                      {section.items.map((item, itemIndex) => (
                        <SettingsItemContainer
                          key={`${section.index}-${item.title}`}
                          setting={item}
                          index={itemIndex}
                          tone={section.tone}
                          handleItemClick={(i, setting) =>
                            handleItemClick(i, setting, section)
                          }
                        />
                      ))}
                    </div>
                  </section>
                );
              })
            )}
          </div>
        </main>
      </div>
    </InnerLayout>
  );
};

export default Settings;
